import { Map, List } from 'immutable'
import { CommonActionTypes } from './constants'

import {
    EXPAND_INTERACTIVE_ELEMENTS_LIST,
    MINIMIZE_INTERACTIVE_ELEMENTS_LIST,
    HIDE_INTERACTIVE_ELEMENTS_LIST,
} from './actions/interactiveElementsListActions'
import { InteractiveListStates, PostionalMediaActions, InteractiveElementActions } from './constants'
import getBool from 'aria-viewer/utils/getBool'
import { getEnv } from 'aria-viewer/utils/environment'
import { isDesktop, isMobileWebSupportedOnDevice } from 'aria-viewer/device/deviceReducer'

const isDesktop3DEnabled = () => getEnv('REACT_APP_DESKTOP_INTERACTIVE_ELEMENTS').toLowerCase() == '3d'

const enable3DElements = isDesktop()
    ? isDesktop3DEnabled() && getBool(getEnv('REACT_APP_ENABLE_3D_INTERACTIVE_ELEMENTS'))
    : isMobileWebSupportedOnDevice() && getBool(getEnv('REACT_APP_ENABLE_3D_INTERACTIVE_ELEMENTS'))

const INITIAL_STATE = Map({
    enable3D: enable3DElements,
    hydrated: false,
    hotspots: Map({
        byId: Map({}), // id: {id, type, position, mediaList: []}
        allIds: List(), // id
    }),
    meta: Map({
        visited: Map({
            byStop: Map(), // stop: [{id, type}, ...]
        }), // {id, type}
        totalElements: Map({
            byStop: Map(), // stop: [{id, type}, ...]
        }),
    }),
    triggers: Map({
        byId: Map({}), // id: {id, type, position, mediaList: []}
        allIds: List(), // id
    }),
    positionalMedia: Map({
        byId: Map({}), // id: {id, type, position, time}
        allIds: List(), // id
    }),
    preview: Map({
        previewType: null,
        id: null,
        type: null,
    }),
    modal: Map({
        id: null,
        type: null,
        mediaId: null,
        mediaType: null,
    }),
    mediaItems: Map({
        byId: Map({}),
        allIds: List(),
    }),
    videoMedia: Map({
        byId: Map({}),
        allIds: List(),
    }),
    stops: Map({
        byId: Map({}),
        allIds: List(),
    }),
    panoramas: Map({
        byId: Map({}),
        allIds: List(),
    }),
    positionalMediaItems: Map({
        byId: Map({}),
        allIds: List(),
    }),
    photoMain: Map({
        byId: Map({}),
        allIds: List(),
    }),
    videoMain: Map({
        byId: Map({}),
        allIds: List(),
    }),
    renditions: Map({
        byId: Map({}),
        allIds: List(),
    }),
    currentScene: Map({
        stopId: null,
        scene: Map({}),
    }),
    forceMediaPause: Map({
        state: null,
    }),
    draggingHint: Map({
        isVisible: true,
        delay: 4500,
    }),
    hotspotCoordinates: Map({
        x: 0,
        y: 0,
    }),
    positionalMediaPlayers: {},
    interactiveElementsListState: InteractiveListStates.HIDDEN,
})

export default (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case InteractiveElementActions.SET_HOTSPOT_COORDINATES: {
            return state.setIn(['hotspotCoordinates'], action.hotspotCoordinates)
        }

        case CommonActionTypes.HYDRATE_INTERACTIVE: {
            return state.mergeDeep(action.data).set('hydrated', true)
        }

        case CommonActionTypes.SET_VISITED: {
            let list = action.visitedByStop || new List([])

            const index = list.findIndex(item => item['id'] === action.data.id && item['type'] === action.data.type)

            if (index === -1) {
                list = list.push({
                    id: action.data.id,
                    type: action.data.type,
                })
            }

            return state.setIn(['meta', 'visited', 'byStop', '' + action.stopid], list)
        }

        case CommonActionTypes.OPEN_PREVIEW: {
            const { el } = action
            return state.setIn(['preview'], el)
        }

        case CommonActionTypes.CLOSE_PREVIEW: {
            return state.setIn(['preview'], INITIAL_STATE.getIn(['preview']))
        }
        case CommonActionTypes.OPEN_MODAL: {
            const {
                el: { type, id, mediaId, mediaType },
            } = action
            return state.setIn(['modal'], { type, id, mediaId, mediaType })
        }

        case CommonActionTypes.CLOSE_MODAL: {
            return state.setIn(['modal'], INITIAL_STATE.getIn(['modal']))
        }

        case CommonActionTypes.SET_SCENE: {
            return state.setIn(['currentScene'], action.scene)
        }

        case CommonActionTypes.SET_DRAG_HINT_DETAILS: {
            return state.mergeIn(['draggingHint'], action.draggingHint)
        }

        case CommonActionTypes.FORCE_MEDIA_PAUSE: {
            const { forceBoolean } = action
            return state.setIn(['forceMediaPause'], { forceBoolean })
        }

        case EXPAND_INTERACTIVE_ELEMENTS_LIST:
            return state.set('interactiveElementsListState', InteractiveListStates.EXPANDED)
        case MINIMIZE_INTERACTIVE_ELEMENTS_LIST:
            return state.set('interactiveElementsListState', InteractiveListStates.MINIMIZED)
        case HIDE_INTERACTIVE_ELEMENTS_LIST:
            return state.set('interactiveElementsListState', InteractiveListStates.HIDDEN)

        case PostionalMediaActions.REGISTER_POSITIONAL_MEDIA:
            return state.update('positionalMediaPlayers', value => ({
                ...value,
                [action.positionalMedia.arg.id]: {
                    play: () => action.positionalMedia.play(),
                    pause: () => action.positionalMedia.pause(),
                    isPlaying: () => action.positionalMedia.isPlaying(),
                    destroy: () => {
                        action.positionalMedia = null
                    },
                    progress: undefined,
                    state: undefined,
                },
            }))
        case PostionalMediaActions.SET_POSITIONAL_MEDIA_STATUS: {
            return state.update('positionalMediaPlayers', value => ({
                ...value,
                [action.id]: {
                    ...value[action.id],
                    state: action.status,
                },
            }))
        }
        case PostionalMediaActions.SET_POSITIONAL_MEDIA_PROGRESS: {
            return state.update('positionalMediaPlayers', value => ({
                ...value,
                [action.id]: {
                    ...value[action.id],
                    progress: action.progress,
                },
            }))
        }
        case PostionalMediaActions.UNREGISTER_POSITIONAL_MEDIA: {
            return state.update('positionalMediaPlayers', value => ({
                ...value,
                [action.id]: undefined,
            }))
        }
        case PostionalMediaActions.RESET: {
            const players = state.get('positionalMediaPlayers')

            if (players) {
                Object.values(players).forEach(pm => pm && pm.destroy && pm.destroy())
            }

            return state.set('positionalMediaPlayers', INITIAL_STATE.positionalMediaPlayers)
        }

        default:
            return state
    }
}
